import { Modifiers, Boundary, Placement } from 'popper.js';
import type { PopoverProps } from '../../Popover.types';

const getUnit = (value?: string | number) => {
  if (!value) {
    return;
  }
  if (typeof value === 'string') {
    return value;
  }
  return `${value}px`;
};

const calculateOffset = ({
  moveBy,
  placement,
}: {
  moveBy: PopoverProps['moveBy'];
  placement: PopoverProps['placement'];
}): string => {
  /*
   * For `right` and `left` placements, we need to flip the `x` and `y` values as Popper.JS will use
   * the first value for the main axis. As per Popper.js docs:
   *
   *   if the placement is top or bottom, the length will be the width. In case of left or right, it
   *   will be the height.
   *
   */
  if (placement?.includes('right') || placement?.includes('left')) {
    return `${moveBy ? moveBy.y : 0}px, ${moveBy ? moveBy.x : 0}px`;
  }

  return `${moveBy ? moveBy.x : 0}px, ${moveBy ? moveBy.y : 0}px`;
};

interface styles {
  minWidth?: string;
  width?: string;
}

const resolveWidth = ({
  width,
  minWidth,
  dynamicWidth,
  referenceWidth,
}: {
  width?: PopoverProps['width'];
  minWidth?: PopoverProps['minWidth'];
  dynamicWidth?: PopoverProps['dynamicWidth'];
  referenceWidth?: number;
}): styles => {
  return {
    minWidth: dynamicWidth ? `${referenceWidth}px` : getUnit(minWidth),
    width: getUnit(width) || 'auto',
  };
};

export type MoveBy = Partial<{ x: number; y: number }>;

export interface ModifiersParams {
  width?: string | number;
  moveBy?: MoveBy;
  appendTo?: Boundary | Element;
  placement: Placement;
  shouldAnimate: boolean;
  flip?: boolean;
  fixed?: boolean;
  isTestEnv: boolean;
  minWidth?: string | number;
  dynamicWidth?: boolean;
}

export const getModifiers = ({
  width,
  moveBy,
  appendTo,
  shouldAnimate,
  flip,
  fixed,
  placement,
  isTestEnv,
  minWidth,
  dynamicWidth,
}: ModifiersParams) => {
  const preventOverflow = !fixed;

  const modifiers: Modifiers = {
    offset: {
      offset: calculateOffset({ moveBy, placement }),
    },
    computeStyle: {
      gpuAcceleration: !shouldAnimate,
    },
    flip: {
      enabled: typeof flip !== 'undefined' ? flip : !moveBy,
    },
    preventOverflow: {
      enabled: preventOverflow,
    },
    hide: {
      enabled: preventOverflow,
    },
  };

  if (dynamicWidth || minWidth || width) {
    modifiers.setPopperWidth = {
      enabled: true,
      order: 840,
      fn: data => {
        const { width: referenceWidth } = data.offsets.reference;
        data.styles = {
          ...data.styles,
          ...resolveWidth({
            width,
            referenceWidth,
            minWidth,
            dynamicWidth,
          }),
        };

        return data;
      },
    };
  }

  if (isTestEnv) {
    modifiers.computeStyle = { enabled: false };
  }

  if (appendTo) {
    modifiers.preventOverflow = {
      ...modifiers.preventOverflow,
      boundariesElement: appendTo,
    };
  }

  return modifiers;
};
